import React, { useEffect, useMemo, useState } from "react";

import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";
import { showGrade } from "../../../utils/textUtils";
import {
  fetchTeacherLevelTestAPI,
  saveLevelTestInviteAPI,
  sendEnrolledNoticeAPI,
  sendLevelTestInviteAPI,
} from "../levelTestAPI";
import "./LevelTestManagement.css";

// 레벨테스트 관리. 세 갈래를 한 화면에서 오간다.
//   재원생    우리 학생과 이어진 응시 기록. 월별 점수 추적표
//   신규생    지인에듀 신청서 + 응시 기록. 응시코드와 결과
//   단어리스트 학생이 신청한 모르는 단어. 제목까지 한 번에 복사한다
//
// 재원생 전환이 신규생 -> 재원생으로 넘어가는 동작이라 세 탭을 한 화면에 뒀다.
// 밖으로 내보내는 주소. 탭마다 하나씩이고, 탭 줄 오른쪽 끝의 버튼이 그것을 복사한다.
const TEST_PLAYER_URL = "https://www.ginedu.com/teacher/test-player.html";
const APPLY_URL = "https://www.ginedu.com/teacher/apply.html";
// 학생이 모르는 단어를 직접 적어 보내는 곳. 여기로 들어온 것이 단어리스트 탭에 쌓인다.
const WORD_TRIAL_URL = "https://www.ginedu.com/teacher/word-free-trial.html";

// 탭 하나에 링크 하나. 안내 문구를 따로 두지 않고 버튼 이름이 그 역할을 한다.
const TAB_LIST = [
  { key: "enrolled", text: "재원생", linkText: "응시링크", linkUrl: TEST_PLAYER_URL },
  { key: "new", text: "신규생", linkText: "신청링크", linkUrl: APPLY_URL },
  {
    key: "words",
    text: "단어리스트",
    linkText: "단어 제출 링크",
    linkUrl: WORD_TRIAL_URL,
  },
];

// "2026-08" -> "8월"
const formatMonthLabel = (month) =>
  month ? `${Number(month.slice(5, 7))}월` : "";

// 신규생 등록 초안의 빈 값. 입력칸이 제어 컴포넌트라 undefined 가 들어가면 안 된다.
//
// 자동 발송 스위치는 여기에 없다. 알림톡 관리의 발송조건('신규생 응시 안내')이
// 학원 단위로 정한다. 등록마다 따로 끄게 하면 어디까지 나갔는지 두 군데를 봐야 한다.
const EMPTY_INVITE = {
  name: "",
  parent_phone: "",
  school: "",
  grade_text: "",
};

const copyToClipboard = (text, onDone) => {
  const fallback = () => {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand("copy");
    } catch (error) {
      // 복사를 막는 브라우저도 있다. 실패해도 화면은 그대로 둔다.
    }
    document.body.removeChild(area);
  };

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(onDone, () => {
      fallback();
      onDone();
    });
    return;
  }

  fallback();
  onDone();
};

const CopyButton = ({ text, label = "복사", className = "level-test-copy" }) => {
  const [isDone, setIsDone] = useState(false);

  return (
    <button
      type="button"
      className={className}
      onClick={() =>
        copyToClipboard(text, () => {
          setIsDone(true);
          setTimeout(() => setIsDone(false), 1500);
        })
      }
    >
      {isDone ? "복사됨" : label}
    </button>
  );
};

const LevelTestManagement = () => {
  const [tab, setTab] = useState("enrolled");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openWordRow, setOpenWordRow] = useState(null);
  // 신규생 등록. 저장한 결과를 여기에 들고 있어야 전체를 다시 안 불러도 화면이 바뀐다.
  const [inviteByCode, setInviteByCode] = useState({});
  const [openInviteCode, setOpenInviteCode] = useState(null);
  const [inviteDraft, setInviteDraft] = useState(EMPTY_INVITE);
  const [sendingCode, setSendingCode] = useState("");
  // 이번 달 응시 안내를 보낸 재원생. 목록을 다시 안 불러도 칸이 바뀌게 들고 있는다.
  const [noticedStudentIdSet, setNoticedStudentIdSet] = useState(() => new Set());
  const [noticingStudentId, setNoticingStudentId] = useState(null);
  // 운영진이 들여다보는 학원. 빈 값이면 자기 학원이다. 학원 계정에는 고를 것이 없다.
  const [viewingNickname, setViewingNickname] = useState("");

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    fetchTeacherLevelTestAPI(viewingNickname)
      .then((result) => {
        if (isCurrent) {
          setData(result);
          // 서버가 준 등록 정보를 초기값으로 깐다. 이후 저장·발송은 이 위에 얹힌다.
          setInviteByCode(
            Object.fromEntries(
              (result.codes || [])
                .filter((row) => row.invite)
                .map((row) => [row.code, row.invite])
            )
          );
          // 학원을 바꿔 보는 중이면 앞 학원에서 눌러 둔 흔적이 남으면 안 된다.
          setNoticedStudentIdSet(new Set());
          setOpenInviteCode(null);
          setOpenWordRow(null);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [viewingNickname]);

  const counts = useMemo(
    () => ({
      enrolled: data?.enrolled?.length || 0,
      new: data?.new_students?.length || 0,
      words: data?.word_trials?.length || 0,
    }),
    [data]
  );

  const activeTab = TAB_LIST.find((item) => item.key === tab) || TAB_LIST[0];

  const handleEnrolledNotice = (studentId) => {
    // disabled 로 막지 않는다. 두 번 눌리는 것만 여기서 막는다.
    if (noticingStudentId) {
      return;
    }
    if (!window.confirm("이번 달 응시 안내 알림톡을 보냅니다.")) {
      return;
    }

    setNoticingStudentId(studentId);
    sendEnrolledNoticeAPI(studentId)
      .then(() => {
        setNoticedStudentIdSet((previous) => new Set(previous).add(studentId));
      })
      .catch((error) => {
        alert(error?.response?.data?.error || "발송에 실패했습니다.");
      })
      .finally(() => setNoticingStudentId(null));
  };

  const openInviteEditor = (code, invite) => {
    if (openInviteCode === code) {
      setOpenInviteCode(null);
      return;
    }
    setInviteDraft(invite ? { ...EMPTY_INVITE, ...invite } : EMPTY_INVITE);
    setOpenInviteCode(code);
  };

  const handleInviteSave = (code) => {
    if (!inviteDraft.name.trim()) {
      alert("학생 이름을 적어주세요.");
      return;
    }

    saveLevelTestInviteAPI({ ...inviteDraft, code })
      .then((saved) => {
        setInviteByCode((previous) => ({ ...previous, [code]: saved }));
        setOpenInviteCode(null);
      })
      .catch((error) => {
        alert(error?.response?.data?.error || "등록에 실패했습니다.");
      });
  };

  const handleInviteSend = (code) => {
    const invite = inviteByCode[code];
    const name = invite ? invite.name : "";
    if (!window.confirm(`${name} 학부모에게 응시 안내 알림톡을 보냅니다.`)) {
      return;
    }

    setSendingCode(code);
    sendLevelTestInviteAPI(code)
      .then((result) => {
        setInviteByCode((previous) => ({
          ...previous,
          [code]: { ...previous[code], ...result },
        }));
      })
      .catch((error) => {
        const message = error?.response?.data?.error || "발송에 실패했습니다.";
        setInviteByCode((previous) => ({
          ...previous,
          [code]: { ...previous[code], send_status: "failed", send_error: message },
        }));
        alert(message);
      })
      .finally(() => setSendingCode(""));
  };

  // 학원을 바꿔 보는 중에는 앞 학원 화면을 그대로 두고 바뀐 것만 갈아끼운다.
  // 통째로 '불러오는 중' 으로 바꾸면 학원 고르는 칸까지 사라져 다시 고를 수가 없다.
  if (!data) {
    return (
      <div className="level-test-page">
        <p className="level-test-empty">
          {isLoading ? "불러오는 중…" : "레벨테스트 기록이 없습니다."}
        </p>
      </div>
    );
  }

  return (
    <div
      className="level-test-page"
      style={{ maxWidth: `${CONTAINER_MAX_WIDTH}px` }}
    >
      <h3 className="section-title level-test-page__title">
        레벨테스트 관리
        {/* 운영진에게만 학원 목록이 온다. 학원 계정에는 이 칸이 아예 안 생긴다. */}
        {(data.academy_list || []).length > 0 && (
          <select
            className="level-test-academy-select"
            value={viewingNickname}
            onChange={(event) => setViewingNickname(event.target.value)}
          >
            <option value="">내 학원</option>
            {data.academy_list.map((item) => (
              <option key={item.nickname} value={item.nickname}>
                {item.academy}
              </option>
            ))}
          </select>
        )}
        {data.is_readonly && (
          <span className="level-test-readonly">
            보기 전용 · {data.academy} 화면을 그대로 봅니다
          </span>
        )}
        {isLoading && <span className="level-test-readonly">불러오는 중…</span>}
        {/* 우리 계정에는 학원이 없다. 고르라고 알려 줘야 빈 화면을 오해하지 않는다. */}
        {!data.academy && (data.academy_list || []).length > 0 && (
          <span className="level-test-readonly">학원을 고르세요</span>
        )}
      </h3>

      {/* 탭과 링크 버튼을 한 줄에 둔다. 좁아지면 버튼이 아래로 내려간다. */}
      <div className="level-test-page__bar">
        <div className="level-test-page__tabs">
          {TAB_LIST.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`level-test-tab${tab === item.key ? " level-test-tab--on" : ""}`}
              onClick={() => setTab(item.key)}
            >
              {item.text}
              <span className="level-test-tab__count">{counts[item.key]}</span>
            </button>
          ))}
        </div>
        {/* 지금 탭의 링크만 내보낸다. 탭과 같은 크기라 한 줄로 읽힌다. */}
        <CopyButton
          className="level-test-link"
          text={activeTab.linkUrl}
          label={activeTab.linkText}
        />
      </div>

      {tab === "enrolled" && (
        <div className="level-test-card">
          <div className="level-test-scroll">
            <table className="level-test-table">
              <colgroup>
                <col style={{ width: "13%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "14%" }} />
                {data.months.map((month) => (
                  <col key={month} />
                ))}
                <col style={{ width: "16%" }} />
              </colgroup>
              <tbody>
                <tr className="level-test-table__head">
                  <td>학생</td>
                  <td>학년</td>
                  <td className="is-center">이달 응시코드</td>
                  {data.months.map((month) => (
                    <td key={month} className="is-center">
                      {formatMonthLabel(month)}
                    </td>
                  ))}
                  <td className="is-center">추출된 단어</td>
                </tr>
                {data.enrolled.map((row) => (
                  <tr key={row.student_id}>
                    <td>
                      {/* 이름을 누르면 그 학생 화면으로 바로 들어간다. */}
                      <a
                        className="level-test-student"
                        href={row.student_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.name}
                      </a>
                    </td>
                    <td>{showGrade(row.grade)}</td>
                    <td className="is-center">
                      {row.month_code ? (
                        <>
                          <span className="level-test-code">{row.month_code}</span>
                          {/* 배정이 아니라 이미 쓴 코드다. 그 학생은 자기 몫을
                              이번 달에 이미 썼다. */}
                          {row.is_code_used && (
                            <div className="level-test-code-used">사용함</div>
                          )}
                        </>
                      ) : (
                        <span className="is-muted">미배정</span>
                      )}
                    </td>
                    {data.months.map((month) => {
                      const result = row.results_by_month[month];
                      if (!result) {
                        // 이번 달을 아직 안 봤으면 여기서 바로 안내를 보낸다.
                        const isCurrentMonth = month === data.current_month;
                        const isNoticed = noticedStudentIdSet.has(row.student_id);

                        return (
                          <td key={month} className="is-center">
                            {/* 남의 학원 학생에게 알림톡을 보낼 수는 없다. */}
                            {!isCurrentMonth || data.is_readonly ? (
                              <span className="is-muted">—</span>
                            ) : isNoticed ? (
                              <span className="level-test-badge">발송됨</span>
                            ) : (
                              <button
                                type="button"
                                className="level-test-send"
                                onClick={() => handleEnrolledNotice(row.student_id)}
                              >
                                {noticingStudentId === row.student_id
                                  ? "보내는 중…"
                                  : "알림톡 발송"}
                              </button>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={month} className="is-center">
                          {result.score_percent === null ? (
                            <span className="level-test-progress">진행중</span>
                          ) : (
                            <b className="level-test-score">
                              {result.score_percent}%
                            </b>
                          )}
                          <div className="level-test-stage">
                            S{String(result.stage).padStart(2, "0")}
                          </div>
                          {result.report_url && (
                            <a
                              className="level-test-report"
                              href={result.report_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              리포트
                            </a>
                          )}
                        </td>
                      );
                    })}
                    <td className="is-center">
                      {row.word_count > 0 ? (
                        <span className="level-test-words">
                          <span className="level-test-words__count">
                            {row.word_count}개
                          </span>
                          <CopyButton text={row.word_title} label="제목" />
                          <CopyButton text={row.words_text} label="단어" />
                        </span>
                      ) : (
                        <span className="is-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "new" && (
        <>
          <div className="level-test-card">
            <div className="level-test-scroll">
              <table className="level-test-table">
                <colgroup>
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                </colgroup>
                <tbody>
                  <tr className="level-test-table__head">
                    <td>이름</td>
                    <td>학년</td>
                    <td>학교</td>
                    <td>학부모 번호</td>
                    <td className="is-center">응시</td>
                    <td className="is-center">점수</td>
                    <td className="is-center">리포트</td>
                  </tr>
                  {data.new_students.map((row) => (
                    <tr key={`${row.name}-${row.grade_text}`}>
                      <td className="is-strong">{row.name}</td>
                      <td>{row.grade_text}</td>
                      <td>{row.school || "—"}</td>
                      <td>
                        {row.parent_phone ? (
                          <span className="level-test-phone">
                            {row.parent_phone}
                            <CopyButton text={row.parent_phone} />
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="is-center">
                        {row.test_count > 0 ? (
                          <span className="level-test-badge">{row.test_count}회</span>
                        ) : (
                          <span className="is-muted">미응시</span>
                        )}
                      </td>
                      <td className="is-center">
                        {row.score_percent === null ? (
                          <span className="is-muted">—</span>
                        ) : (
                          <b className="level-test-score">{row.score_percent}%</b>
                        )}
                      </td>
                      <td className="is-center">
                        {row.report_url ? (
                          <a
                            className="level-test-report"
                            href={row.report_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            리포트
                          </a>
                        ) : (
                          <span className="is-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h4 className="level-test-subtitle">
            응시코드
            <span className="level-test-subtitle__count">{data.codes.length}개</span>
            <span className="level-test-subtitle__note">
              우리 학원에 발급된 코드입니다. 신규생을 등록해 두면 응시 안내 알림톡을
              보낼 수 있습니다.
            </span>
          </h4>
          <div className="level-test-card">
            <div className="level-test-scroll">
              <table className="level-test-table">
                <colgroup>
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "23%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <tbody>
                  <tr className="level-test-table__head">
                    <td>응시코드</td>
                    <td>받는 학생</td>
                    <td className="is-center">알림톡</td>
                    <td className="is-center">만료</td>
                    <td>응시자</td>
                    <td className="is-center">점수</td>
                    <td className="is-center">리포트</td>
                    <td className="is-center">응시</td>
                  </tr>
                  {data.codes.slice(0, 50).map((row) => {
                    const invite = inviteByCode[row.code] || row.invite;
                    const isOpen = openInviteCode === row.code;

                    return (
                      <React.Fragment key={row.code}>
                        <tr className={invite ? "level-test-row--invited" : ""}>
                          <td className="level-test-code">{row.code}</td>
                          <td>
                            {/* 등록해 두면 이름이 뜨고 줄 색이 바뀐다.
                                남의 학원을 볼 때는 등록을 열지 않는다. */}
                            <button
                              type="button"
                              className="level-test-invite-open"
                              onClick={() =>
                                !data.is_readonly &&
                                openInviteEditor(row.code, invite)
                              }
                            >
                              {invite ? (
                                <>
                                  <b>{invite.name}</b>
                                  <span className="is-muted">
                                    {[invite.grade_text, invite.school]
                                      .filter(Boolean)
                                      .join(" · ") || "정보 없음"}
                                  </span>
                                </>
                              ) : data.is_readonly ? (
                                <span className="is-muted">—</span>
                              ) : (
                                <span className="level-test-invite-add">
                                  + 학생 등록
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="is-center">
                            {!invite || data.is_readonly ? (
                              <span className="is-muted">—</span>
                            ) : invite.send_status === "sent" ? (
                              <span className="level-test-badge">발송됨</span>
                            ) : (
                              <button
                                type="button"
                                className={`level-test-send${
                                  invite.send_status === "failed"
                                    ? " level-test-send--failed"
                                    : ""
                                }`}
                                disabled={sendingCode === row.code}
                                title={invite.send_error || ""}
                                onClick={() => handleInviteSend(row.code)}
                              >
                                {sendingCode === row.code
                                  ? "보내는 중…"
                                  : invite.send_status === "failed"
                                    ? "다시 발송"
                                    : "알림톡 발송"}
                              </button>
                            )}
                          </td>
                          <td className="is-center is-muted">{row.expires || "—"}</td>
                          <td>
                            {row.used_by || "—"}
                            {row.used_grade && (
                              <span className="is-muted"> {row.used_grade}</span>
                            )}
                          </td>
                          <td className="is-center">
                            {row.score_percent === null ? (
                              <span className="is-muted">—</span>
                            ) : (
                              <b className="level-test-score">{row.score_percent}%</b>
                            )}
                          </td>
                          <td className="is-center">
                            {row.report_url ? (
                              <a
                                className="level-test-report"
                                href={row.report_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                리포트
                              </a>
                            ) : (
                              <span className="is-muted">—</span>
                            )}
                          </td>
                          <td className="is-center">
                            {row.is_used ? (
                              <span className="level-test-badge">사용완료</span>
                            ) : (
                              <span className="is-muted">미사용</span>
                            )}
                          </td>
                        </tr>

                        {isOpen && (
                          <tr className="level-test-invite-row">
                            <td colSpan={8}>
                              <div className="level-test-invite">
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학생 이름"
                                  value={inviteDraft.name}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      name: event.target.value,
                                    }))
                                  }
                                />
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학부모 번호"
                                  value={inviteDraft.parent_phone}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      parent_phone: event.target.value,
                                    }))
                                  }
                                />
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학교"
                                  value={inviteDraft.school}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      school: event.target.value,
                                    }))
                                  }
                                />
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학년 (예: 중등 1학년)"
                                  value={inviteDraft.grade_text}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      grade_text: event.target.value,
                                    }))
                                  }
                                />
                                <button
                                  type="button"
                                  className="level-test-invite__save"
                                  onClick={() => handleInviteSave(row.code)}
                                >
                                  등록
                                </button>
                                <button
                                  type="button"
                                  className="level-test-invite__cancel"
                                  onClick={() => setOpenInviteCode(null)}
                                >
                                  취소
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "words" && (
        <>
          <h4 className="level-test-subtitle">
            모르는 단어
            <span className="level-test-subtitle__note">
              제목과 단어를 따로 복사합니다. 지인에듀 단어장에 제목 한 번, 단어 한 번 붙여넣으면 됩니다.
            </span>
          </h4>

          {data.word_trials.length === 0 && (
            <p className="level-test-empty">아직 신청된 단어가 없습니다.</p>
          )}

          {data.word_trials.map((row, index) => (
            <div key={`${row.name}-${index}`} className="level-test-card word-trial">
              <div className="word-trial__head">
                <button
                  type="button"
                  className="word-trial__toggle"
                  onClick={() =>
                    setOpenWordRow(openWordRow === index ? null : index)
                  }
                >
                  <span className="is-strong">{row.title}</span>
                  <span className="word-trial__meta">
                    {row.grade} · {row.school || "학교 미기재"}
                  </span>
                  <span className="word-trial__count">{row.word_count}개</span>
                  <span className="word-trial__caret">
                    {openWordRow === index ? "▲" : "▼"}
                  </span>
                </button>
                <CopyButton text={row.title} label="제목 복사" />
                <CopyButton text={row.words_text} label="단어 복사" />
              </div>
              {openWordRow === index && (
                <textarea
                  className="word-trial__words"
                  readOnly
                  value={row.words_text}
                />
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default LevelTestManagement;
